import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TabCountersContextValue } from '@lib/contexts/TabCountersContext';

type Args = {
  /**
   * `loading` of that query. Only suppresses the navigate-triggered refetch:
   * a fetch already in flight when the tab changes will deliver fresh counters
   * on its own. Worth passing when the query's variables differ between tabs
   * (Apollo then refetches by itself and this would double the request);
   * `refetchCounters()` ignores it, since a just-completed mutation can't be
   * covered by a request that started before it.
   */
  loading?: boolean;
  /** `refetch` of the query that provides the tab-bar counters. */
  refetch?: () => Promise<unknown>;
  /** Mirrors the `skip` of that query — nothing to refetch while it's true. */
  skip?: boolean;
};

/**
 * Keeps tab-bar counters fresh on pages where every tab is its own route but
 * all of them render the same shell component (see `SiteTabsPage`). Because the
 * shell reconciles instead of remounting on a tab click, its counters query
 * never refires and the numbers stay frozen at whatever the Apollo cache held
 * when the page was first opened.
 *
 * Two refresh triggers, both needed:
 * - **on navigate** — switching tabs inside the shell refetches, so anything
 *   mutated in another tab (or another browser tab) shows up on arrival;
 * - **on demand** — `refetchCounters()` from `useTabCountersContext()`, for
 *   mutations made in the currently visible tab whose response can't repair the
 *   counter in the cache (bulk destroys that return a scalar, writes to a
 *   nested collection, …). Mutations that return the parent entity with its
 *   counter field don't need it: the cache normalises the fresh value already.
 *
 * Pass the returned value straight to `TabCountersContext.Provider`.
 */
export default function useTabCounters({
  loading = false,
  refetch,
  skip = false,
}: Args): TabCountersContextValue {
  const { pathname } = useLocation();

  // `refetch` isn't reference-stable across renders, so keep it in a ref and
  // expose a stable callback that is safe to use as an effect/memo dependency.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const refetchCounters = useCallback(() => {
    if (skip) return;
    refetchRef.current?.();
  }, [skip]);

  // Only the pathname is watched: tab values carry the list query params, so
  // paginating or searching inside a tab must not trigger a counters refetch.
  const lastPathnameRef = useRef(pathname);
  useEffect(() => {
    if (lastPathnameRef.current === pathname) return;
    lastPathnameRef.current = pathname;
    if (loading) return;
    refetchCounters();
  }, [loading, pathname, refetchCounters]);

  return useMemo(() => ({ refetchCounters }), [refetchCounters]);
}
